/**
 * My Tickets Page
 * Lists all active tickets for the user
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  Ticket, 
  Calendar, 
  Clock, 
  MapPin, 
  ChevronRight,
  Loader2,
  QrCode,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyTickets() {
  const navigate = useNavigate();
  const { session } = useAuth();
  
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchTickets = async () => {
      if (!session?.access_token) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/tickets/my-tickets`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch tickets');
        }
        
        const data = await response.json();
        setTickets(data.tickets || []);
      } catch (err) {
        console.error('Tickets fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTickets();
  }, [session?.access_token]);
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  // Format time
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    if (timeStr.includes('T')) {
      return new Date(timeStr).toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    }
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin mx-auto mb-4" />
          <p className="text-soft-silver">Loading your tickets...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="my-tickets-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-cloud-white">My Tickets</h1>
          <p className="text-soft-silver">Your upcoming event tickets</p>
        </div>
      </div>
      
      {/* Tickets List */}
      {error ? (
        <Card className="bg-graphite/50 border-coral-red/30">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-coral-red mx-auto mb-4" />
            <h2 className="text-lg font-medium text-cloud-white mb-2">Failed to load tickets</h2>
            <p className="text-soft-silver">{error}</p>
          </CardContent>
        </Card>
      ) : tickets.length === 0 ? (
        <Card className="bg-graphite/50 border-white/[0.08]">
          <CardContent className="py-12 text-center">
            <Ticket className="w-12 h-12 text-muted-text mx-auto mb-4" />
            <h2 className="text-lg font-medium text-cloud-white mb-2">No Active Tickets</h2>
            <p className="text-soft-silver mb-4">You don't have any upcoming event tickets yet.</p>
            <Button onClick={() => navigate('/discover')}>
              Browse Events
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket) => {
            const event = ticket.events || {};
            const org = event.organizations || {};
            
            return (
              <Link
                key={ticket.id}
                to={`/app/my-ticket/${ticket.id}`}
                className="block"
                data-testid={`ticket-${ticket.id}`}
              >
                <Card className="bg-graphite/50 border-white/[0.08] hover:border-electric-ice-blue/30 transition-colors cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* QR Icon */}
                      <div className="w-16 h-16 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center flex-shrink-0">
                        <QrCode className="w-8 h-8 text-electric-ice-blue" />
                      </div>
                      
                      {/* Event Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="bg-electric-ice-blue/10 text-electric-ice-blue border-0 text-xs">
                            {org.name || 'Event'}
                          </Badge>
                          <Badge className={cn(
                            "border-0 text-xs",
                            ticket.status === 'active' 
                              ? "bg-cool-mint/10 text-cool-mint"
                              : ticket.status === 'checked_in'
                              ? "bg-blue-500/10 text-blue-400"
                              : "bg-muted-text/10 text-muted-text"
                          )}>
                            {ticket.status?.replace('_', ' ')}
                          </Badge>
                        </div>
                        
                        <h3 className="font-semibold text-cloud-white truncate">
                          {event.event_name || 'Event'}
                        </h3>
                        
                        <div className="flex flex-wrap gap-3 mt-1 text-xs text-soft-silver">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>{formatDate(event.date)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{formatTime(event.start_time)}</span>
                          </div>
                          {event.location && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              <span className="truncate max-w-[150px]">{event.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Arrow */}
                      <ChevronRight className="w-5 h-5 text-muted-text flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
